// src/components/financials/FundTransferForm.js
// Updated: 01/25/2026 - Implements Atomic Transactions (Batch Writes) for Data Integrity

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, Button, 
  MenuItem, Alert, ToggleButton, ToggleButtonGroup 
} from '@mui/material';
import { SwapHoriz, Save, Build } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, doc, writeBatch, serverTimestamp, increment } from 'firebase/firestore'; // Import writeBatch & increment
import { useAuth } from '../../context/AuthContext';

const FundTransferForm = ({ onSuccess }) => {
  const { userProfile, currentUser } = useAuth();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('transfer'); 

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    fromFundId: '',
    toFundId: '',
    targetFundId: '', 
    adjustmentType: 'increase',
    amount: '',
    memo: ''
  });

  const churchId = userProfile?.churchId;

  useEffect(() => {
    if (churchId) fetchFunds();
  }, [churchId]);

  const fetchFunds = async () => {
    const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
    setFunds(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const getFundName = (id) => funds.find(f => f.id === id)?.name || 'Unknown';

  const handleSubmit = async () => {
    setError('');
    
    if (!formData.amount || !formData.memo) {
        setError("Amount and Memo are required.");
        return;
    }
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
        setError("Amount must be valid.");
        return;
    }

    setLoading(true);
    try {
        // --- START ATOMIC BATCH ---
        const batch = writeBatch(db); 
        const batchId = `JOURNAL_${Date.now()}`;
        
        // Base Transaction Object
        const baseTx = {
            batchId: 'journal_entry',
            date: formData.date,
            status: 'posted',
            reconciled: false, // Pending Audit
            reconciledAt: null,
            reconciledBy: null,
            note: `${mode === 'transfer' ? 'Transfer' : 'Adj'}: ${formData.memo}`,
            type: mode, 
            transferGroupId: batchId,
            createdAt: serverTimestamp(),
            createdBy: currentUser.uid
        };

        if (mode === 'transfer') {
            // --- TRANSFER LOGIC ---
            if (!formData.fromFundId || !formData.toFundId) throw new Error("Select both funds.");
            if (formData.fromFundId === formData.toFundId) throw new Error("Cannot transfer to same fund.");

            // 1. Transaction Record (OUT)
            const refOut = doc(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'));
            batch.set(refOut, {
                ...baseTx,
                method: 'Internal Transfer',
                donorName: 'Church (Internal)',
                amount: -amount,
                fundId: formData.fromFundId,
                splits: [{ fundId: formData.fromFundId, fundName: getFundName(formData.fromFundId), amount: -amount }]
            });

            // 2. Fund Balance Update (OUT) - AUTOMATIC
            const fundRefOut = doc(db, 'churches', churchId, 'financials', 'data', 'funds', formData.fromFundId);
            batch.update(fundRefOut, { 
                balance: increment(-amount),        // Standard field
                currentBalance: increment(-amount)  // Legacy field support
            });

            // 3. Transaction Record (IN)
            const refIn = doc(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'));
            batch.set(refIn, {
                ...baseTx,
                method: 'Internal Transfer',
                donorName: 'Church (Internal)',
                amount: amount,
                fundId: formData.toFundId,
                splits: [{ fundId: formData.toFundId, fundName: getFundName(formData.toFundId), amount: amount }]
            });

            // 4. Fund Balance Update (IN) - AUTOMATIC
            const fundRefIn = doc(db, 'churches', churchId, 'financials', 'data', 'funds', formData.toFundId);
            batch.update(fundRefIn, { 
                balance: increment(amount),
                currentBalance: increment(amount)
            });

        } else {
            // --- ADJUSTMENT LOGIC ---
            if (!formData.targetFundId) throw new Error("Select a fund to adjust.");
            
            const finalAmount = formData.adjustmentType === 'increase' ? amount : -amount;

            // 1. Transaction Record
            const refAdj = doc(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'));
            batch.set(refAdj, {
                ...baseTx,
                method: 'Fund Adjustment',
                donorName: 'System Adjustment',
                amount: finalAmount,
                fundId: formData.targetFundId,
                splits: [{ fundId: formData.targetFundId, fundName: getFundName(formData.targetFundId), amount: finalAmount }]
            });

            // 2. Fund Balance Update - AUTOMATIC
            const fundRef = doc(db, 'churches', churchId, 'financials', 'data', 'funds', formData.targetFundId);
            batch.update(fundRef, { 
                balance: increment(finalAmount),
                currentBalance: increment(finalAmount)
            });
        }

        // --- COMMIT ATOMIC BATCH ---
        // This executes all steps above. If one fails, they ALL fail. No partial data.
        await batch.commit();

        alert("Entry Posted & Fund Balances Updated Successfully");
        setFormData({ ...formData, amount: '', memo: '' });
        if (onSuccess) onSuccess();
        // Refresh funds list to show new balances immediately
        fetchFunds();

    } catch (e) {
        setError(e.message);
    }
    setLoading(false);
  };

  return (
    <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid', borderColor: mode === 'transfer' ? 'primary.main' : 'warning.main' }}>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {mode === 'transfer' ? <SwapHoriz color="primary" sx={{ mr: 1, fontSize: 30 }} /> : <Build color="warning" sx={{ mr: 1, fontSize: 30 }} />}
                <Typography variant="h6">
                    {mode === 'transfer' ? "Internal Transfer" : "Balance Adjustment"}
                </Typography>
            </Box>
            
            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(e, val) => val && setMode(val)}
                size="small"
            >
                <ToggleButton value="transfer">Transfer</ToggleButton>
                <ToggleButton value="adjustment">Correction / Adj</ToggleButton>
            </ToggleButtonGroup>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} alignItems="center">
            
            <Grid item xs={12} md={3}>
                <TextField 
                    fullWidth type="date" label="Date" 
                    InputLabelProps={{shrink:true}}
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                />
            </Grid>

            {mode === 'transfer' ? (
                <>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            select fullWidth label="From Fund" 
                            value={formData.fromFundId}
                            onChange={(e) => setFormData({...formData, fromFundId: e.target.value})}
                        >
                            {funds.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name} (${(f.balance||0).toLocaleString()})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
                        <SwapHoriz color="action" />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            select fullWidth label="To Fund" 
                            value={formData.toFundId}
                            onChange={(e) => setFormData({...formData, toFundId: e.target.value})}
                        >
                            {funds.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name} (${(f.balance||0).toLocaleString()})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </>
            ) : (
                <>
                    <Grid item xs={12} md={4}>
                        <TextField 
                            select fullWidth label="Fund to Correct" 
                            value={formData.targetFundId}
                            onChange={(e) => setFormData({...formData, targetFundId: e.target.value})}
                        >
                            {funds.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name} (${(f.balance||0).toLocaleString()})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            select fullWidth label="Action" 
                            value={formData.adjustmentType}
                            onChange={(e) => setFormData({...formData, adjustmentType: e.target.value})}
                        >
                            <MenuItem value="increase">Increase Balance (+)</MenuItem>
                            <MenuItem value="decrease">Decrease Balance (-)</MenuItem>
                        </TextField>
                    </Grid>
                </>
            )}

            <Grid item xs={12} md={2}>
                <TextField 
                    fullWidth label="Amount" type="number" 
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    InputProps={{ startAdornment: '$' }}
                />
            </Grid>

            <Grid item xs={12}>
                <TextField 
                    fullWidth label={mode === 'transfer' ? "Reason for Transfer" : "Reason for Correction"}
                    placeholder={mode === 'transfer' ? "e.g. Reimbursing General Fund" : "e.g. Correcting Bank Deposit Error"}
                    value={formData.memo}
                    onChange={(e) => setFormData({...formData, memo: e.target.value})}
                />
            </Grid>
            
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
                <Button 
                    variant="contained" 
                    color={mode === 'transfer' ? 'primary' : 'warning'}
                    startIcon={<Save />}
                    disabled={loading}
                    onClick={handleSubmit}
                >
                    {mode === 'transfer' ? "Post Transfer" : "Post Adjustment"}
                </Button>
            </Grid>
        </Grid>
    </Paper>
  );
};

export default FundTransferForm;